import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { LinearProgress, Popover, Tooltip } from '@mui/material';
import { MessageTestingSocketEvent } from '@trinity/chat';
import type {
  MessageTestingCell,
  MessageTestingProgress,
  MessageTestingResult,
  Score,
} from '@trinity/chat/types/message-testing';
import { useMessageTestingSocket } from '@trinity/chat-components/websocket';
import type { SegmentOption, TestableMessage } from './types';

type RunStatus = 'connecting' | 'processing' | 'completed' | 'failed';

interface MessageTestingResultsPanelProps {
  runIdentifier: string;
  onClose: () => void;
  messages: TestableMessage[];
  segments: SegmentOption[];
  brand?: string;
  objective?: string;
}

export function MessageTestingResultsPanel({
  runIdentifier,
  onClose,
  messages,
  segments,
  brand,
  objective,
}: MessageTestingResultsPanelProps): React.JSX.Element {
  const socket = useMessageTestingSocket(runIdentifier);
  const [status, setStatus] = useState<RunStatus>('connecting');
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [cells, setCells] = useState<MessageTestingCell[]>([]);
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!socket) return;

    setStatus('processing');

    const handleProgress = (payload: MessageTestingProgress): void => {
      setProgress({ completed: payload.completed, total: payload.total });
      if (payload.latestCell) {
        setCells((prev) => [...prev, payload.latestCell!]);
      }
    };

    const handleCellError = (payload: { messageId: string; segmentId: string; error: string }): void => {
      setCellErrors((prev) => {
        const next = new Map(prev);
        next.set(`${payload.messageId}:${payload.segmentId}`, payload.error);
        return next;
      });
    };

    const handleCompleted = (payload: Pick<MessageTestingResult, 'runIdentifier' | 'matrix'>): void => {
      setCells(payload.matrix);
      setProgress({ completed: payload.matrix.length, total: payload.matrix.length });
      setStatus('completed');
    };

    const handleFailed = (payload: { runIdentifier: string; error: string }): void => {
      setError(payload.error);
      setStatus('failed');
    };

    socket.on(MessageTestingSocketEvent.Progress, handleProgress);
    socket.on(MessageTestingSocketEvent.CellError, handleCellError);
    socket.on(MessageTestingSocketEvent.Completed, handleCompleted);
    socket.on(MessageTestingSocketEvent.Failed, handleFailed);

    return () => {
      socket.off(MessageTestingSocketEvent.Progress, handleProgress);
      socket.off(MessageTestingSocketEvent.CellError, handleCellError);
      socket.off(MessageTestingSocketEvent.Completed, handleCompleted);
      socket.off(MessageTestingSocketEvent.Failed, handleFailed);
    };
  }, [socket]);

  // Build a lookup: messageId → segmentId → cell
  const cellMap = useMemo(() => {
    const map = new Map<string, Map<string, MessageTestingCell>>();
    for (const cell of cells) {
      if (!map.has(cell.messageId)) map.set(cell.messageId, new Map());
      map.get(cell.messageId)!.set(cell.segmentId, cell);
    }
    return map;
  }, [cells]);

  const progressPercent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <Root>
      <PanelHeader>
        <PanelTitle>Message Testing Results</PanelTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </PanelHeader>

      {(status === 'connecting' || status === 'processing') && (
        <ProgressSection>
          <ProgressLabel>
            {status === 'connecting'
              ? 'Connecting...'
              : progress.completed > 0 && progress.completed === progress.total
                ? `Complete — ${progress.total} pairs processed`
                : progress.total === 0
                  ? 'Processing...'
                  : `Processing ${progress.completed} / ${progress.total} pairs`}
          </ProgressLabel>
          <LinearProgress variant="determinate" value={progressPercent} />
        </ProgressSection>
      )}

      {status === 'failed' && <ErrorBanner>Simulation failed: {error ?? 'Unknown error'}</ErrorBanner>}

      {(brand || objective) && (
        <RunMeta>
          {brand && (
            <RunMetaItem>
              <strong>Brand:</strong> {brand}
            </RunMetaItem>
          )}
          {objective && (
            <RunMetaItem>
              <strong>Objective:</strong> {objective}
            </RunMetaItem>
          )}
        </RunMeta>
      )}

      {(cells.length > 0 || messages.length > 0) && (
        <GridScroller>
          <PivotTable>
            <thead>
              <tr>
                <MessageHeaderCell>Message</MessageHeaderCell>
                {segments.map((seg) => (
                  <SegmentHeaderCell key={seg.identifier}>{seg.name}</SegmentHeaderCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {messages.map((msg) => {
                const msgId = msg.id;
                return (
                  <tr key={msgId}>
                    <MessageNameCell>
                      <Tooltip title={msg.body} placement="right" enterDelay={300}>
                        <MessageName>{msg.displayTitle}</MessageName>
                      </Tooltip>
                    </MessageNameCell>
                    {segments.map((seg) => {
                      const cell = cellMap.get(msgId)?.get(seg.identifier);
                      const cellError = cellErrors.get(`${msgId}:${seg.identifier}`);
                      return (
                        <td key={seg.identifier}>
                          {cell ? (
                            <ScoreBadge cell={cell} />
                          ) : cellError ? (
                            <ErrorCell>Error</ErrorCell>
                          ) : (
                            <PendingCell>—</PendingCell>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </PivotTable>
        </GridScroller>
      )}
    </Root>
  );
}

// ─── Score Badge with Popover ───────────────────────────────────────────────

const SCORE_LABELS: { key: keyof MessageTestingCell['scores']; label: string }[] = [
  { key: 'personaResonance', label: 'Resonance' },
  { key: 'objectiveAlignment', label: 'Objective Alignment' },
  { key: 'categoryFit', label: 'Category Fit' },
  { key: 'predictedEngagement', label: 'Engagement' },
  { key: 'predictedActionLikelihood', label: 'Action Likelihood' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'credibility', label: 'Credibility' },
  { key: 'cognitiveLoad', label: 'Cognitive Load' },
  { key: 'differentiation', label: 'Differentiation' },
  { key: 'ctaStrength', label: 'CTA Strength' },
  { key: 'toneFit', label: 'Tone Fit' },
];

function computeOverallScore(scores: MessageTestingCell['scores']): number {
  const values = Object.values(scores).map((s: Score) => s.value);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function ScoreBadge({ cell }: { cell: MessageTestingCell }): React.JSX.Element {
  const overall = computeOverallScore(cell.scores);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <BadgeContainer $score={overall} ref={anchorRef} onClick={handleOpen}>
        <BadgeScore>{overall}</BadgeScore>
        <BadgeVerdict>{toTitleCase(cell.overallAssessment.verdict.replace(/_/g, ' '))}</BadgeVerdict>
      </BadgeContainer>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        marginThreshold={16}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 520,
              maxHeight: 'calc(100vh - 32px)',
              overflow: 'auto',
              borderRadius: '10px',
            },
          },
        }}
      >
        <PopoverContent>
          {/* ── Header ── */}
          <PopoverHeader>
            <PopoverVerdict $verdict={cell.overallAssessment.verdict}>
              {toTitleCase(cell.overallAssessment.verdict.replace(/_/g, ' '))}
            </PopoverVerdict>
            <PopoverOverallScore $score={overall}>{overall}</PopoverOverallScore>
          </PopoverHeader>
          <PopoverSummary>{cell.overallAssessment.oneLineSummary}</PopoverSummary>

          {/* ── Strengths & Weaknesses ── */}
          <PopoverSection>
            <PopoverCallout $type="strength">
              <strong>Top Strength:</strong> {cell.overallAssessment.topStrength}
            </PopoverCallout>
            <PopoverCallout $type="weakness">
              <strong>Top Weakness:</strong> {cell.overallAssessment.topWeakness}
            </PopoverCallout>
          </PopoverSection>

          {/* ── Scores Grid ── */}
          <PopoverSectionTitle>Scores</PopoverSectionTitle>
          <ScoresColumns>
            {SCORE_LABELS.map(({ key, label }) => (
              <ScoreItemPopover key={key} label={label} score={cell.scores[key]} />
            ))}
          </ScoresColumns>

          {/* ── Behavioral Predictions ── */}
          {/* <PopoverSectionTitle>Behavioral Predictions</PopoverSectionTitle>
          <PopoverSection>
            <PopoverDetail>
              <strong>Immediate Reaction:</strong>{' '}
              {cell.behavioralPredictions.immediateReaction.replace(/_/g, ' ')}
            </PopoverDetail>
            <PopoverDetail>
              <strong>Likely Follow-up:</strong> {cell.behavioralPredictions.likelyFollowupQuestion}
            </PopoverDetail>
            <PopoverDetail>
              <strong>Behavior Change Horizon:</strong>{' '}
              {cell.behavioralPredictions.behaviorChangeHorizon.replace(/_/g, ' ')}
            </PopoverDetail>
            {cell.behavioralPredictions.infoTheyWouldVerify.length > 0 && (
              <PopoverDetail>
                <strong>Would Verify:</strong> {cell.behavioralPredictions.infoTheyWouldVerify.join(', ')}
              </PopoverDetail>
            )}
          </PopoverSection> */}

          {/* ── Persona Attributes ── */}
          {cell.attributeUtilization.invoked.length > 0 && (
            <>
              <PopoverSectionTitle>Resonated Attributes</PopoverSectionTitle>
              <ChipList>
                {cell.attributeUtilization.invoked.map((attr) => (
                  <AttributeChip key={attr} $type="invoked">
                    {attr}
                  </AttributeChip>
                ))}
              </ChipList>
            </>
          )}

          {/* ── Tone Fit ── */}
          {/* <PopoverSectionTitle>Tone & Perception</PopoverSectionTitle>
          <PopoverSection>
            <PopoverDetail>
              <strong>Detected Tone:</strong> {cell.toneFitDetail.detectedTone}
            </PopoverDetail>
            <PopoverDetail>
              <strong>Preferred Tone:</strong> {cell.toneFitDetail.preferredTone}
            </PopoverDetail>
            <PopoverDetail>
              <strong>Perceived Rep Posture:</strong>{' '}
              {cell.toneFitDetail.perceivedRepPosture.replace(/_/g, ' ')}
            </PopoverDetail>
          </PopoverSection> */}

          {/* ── Trigger Language (hidden to save space) ──
          {(cell.triggerLanguage.positive.length > 0 || cell.triggerLanguage.negative.length > 0) && (
            <>
              <PopoverSectionTitle>Trigger Language</PopoverSectionTitle>
              <PopoverSection>
                {cell.triggerLanguage.positive.map((t, i) => (
                  <TriggerItem key={`pos-${i}`} $type="positive">
                    <TriggerPhrase>&ldquo;{t.phrase}&rdquo;</TriggerPhrase>
                    <TriggerReason>{t.why}</TriggerReason>
                  </TriggerItem>
                ))}
                {cell.triggerLanguage.negative.map((t, i) => (
                  <TriggerItem key={`neg-${i}`} $type="negative">
                    <TriggerPhrase>&ldquo;{t.phrase}&rdquo;</TriggerPhrase>
                    <TriggerReason>{t.why}</TriggerReason>
                  </TriggerItem>
                ))}
              </PopoverSection>
            </>
          )}
          ── */}

          {/* ── Objections ── */}
          {cell.objections.length > 0 && (
            <CollapsibleSection>
              <CollapsibleSummary>Objections ({cell.objections.length})</CollapsibleSummary>
              <PopoverSection>
                {cell.objections.map((obj, i) => (
                  <ObjectionItem key={i}>
                    <ObjectionTitle>
                      {obj.category.replace(/_/g, ' ')}
                      <ObjectionSeverity $severity={obj.severity}>({obj.severity})</ObjectionSeverity>
                    </ObjectionTitle>
                    <PopoverDetail>{obj.text}</PopoverDetail>
                    {obj.suggestedRebuttal && (
                      <PopoverDetail>
                        <strong>Rebuttal:</strong> {obj.suggestedRebuttal}
                      </PopoverDetail>
                    )}
                  </ObjectionItem>
                ))}
              </PopoverSection>
            </CollapsibleSection>
          )}

          {/* ── Improvement Suggestions ── */}
          {cell.improvementSuggestions.length > 0 && (
            <CollapsibleSection>
              <CollapsibleSummary>
                Suggested Improvements ({cell.improvementSuggestions.length})
              </CollapsibleSummary>
              <PopoverSection>
                {cell.improvementSuggestions.map((sug, i) => (
                  <PopoverDetail key={i}>
                    <strong>{toTitleCase(sug.predictedScoreLift)} Impact:</strong> {sug.change}
                    <br />
                    <span style={{ color: '#888', fontSize: '10px' }}>{sug.rationale}</span>
                  </PopoverDetail>
                ))}
              </PopoverSection>
            </CollapsibleSection>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

// ─── Score Item Popover ─────────────────────────────────────────────────────

function ScoreItemPopover({ label, score }: { label: string; score: Score }): React.JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <ScoreCompactItem ref={anchorRef} onClick={handleOpen} style={{ cursor: 'pointer' }}>
        <ScoreCompactLabel>{label}</ScoreCompactLabel>
        <ScoreCompactValue $score={score.value}>{score.value}</ScoreCompactValue>
      </ScoreCompactItem>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 360,
              maxHeight: 'calc(100vh - 32px)',
              overflow: 'auto',
              borderRadius: '10px',
            },
          },
        }}
      >
        <ScorePopoverContent>
          <ScorePopoverHeader>
            <ScorePopoverLabel>{label}</ScorePopoverLabel>
            <ScorePopoverValue $score={score.value}>{score.value}</ScorePopoverValue>
          </ScorePopoverHeader>

          <ScoreConfidence>
            <strong>Confidence:</strong> {Math.round(score.confidence * 100)}%
          </ScoreConfidence>

          <ScoreReasoning>{score.reasoning}</ScoreReasoning>

          {score.evidence.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Evidence</ScorePopoverSectionTitle>
              {score.evidence.map((e, i) => (
                <EvidenceItem key={i} $impact={e.impact}>
                  <EvidenceQuote>&ldquo;{e.quote}&rdquo;</EvidenceQuote>
                  <EvidenceMeta>
                    <EvidenceSource>{e.source.replace(/([A-Z])/g, ' $1').trim()}</EvidenceSource>
                    {/* <EvidenceImpact $impact={e.impact}>{e.impact}</EvidenceImpact> */}
                  </EvidenceMeta>
                </EvidenceItem>
              ))}
            </>
          )}

          {score.personaAttributesUsed.length > 0 && (
            <>
              <ScorePopoverSectionTitle>Persona Attributes</ScorePopoverSectionTitle>
              <ChipList>
                {score.personaAttributesUsed.map((attr) => (
                  <AttributeChip key={attr} $type="invoked">
                    {attr}
                  </AttributeChip>
                ))}
              </ChipList>
            </>
          )}
        </ScorePopoverContent>
      </Popover>
    </>
  );
}

// ─── Color palette ──────────────────────────────────────────────────────────

const COLORS = {
  green: { text: '#065f46', bg: '#ecfdf5', border: '#a7f3d0', accent: '#34d399' },
  yellow: { text: '#ffa700', bg: '#fefce8', border: '#fde68a' },
  red: { text: '#dc2626', bg: '#fef2f2', border: '#fecaca', accent: '#f87171', dark: '#991b1b' },
  neutral: { text: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
} as const;

// ─── Color helpers ──────────────────────────────────────────────────────────

function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function scoreColor(score: number): string {
  if (score >= 70) return COLORS.green.text;
  if (score >= 50) return COLORS.yellow.text;
  return COLORS.red.text;
}

function scoreBg(score: number): string {
  if (score >= 70) return COLORS.green.bg;
  if (score >= 50) return COLORS.yellow.bg;
  return COLORS.red.bg;
}

function scoreBorder(score: number): string {
  if (score >= 70) return COLORS.green.border;
  if (score >= 50) return COLORS.yellow.border;
  return COLORS.red.border;
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;

  &:hover {
    color: #1a1a1a;
  }
`;

const ProgressSection = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
`;

const ErrorBanner = styled.div`
  padding: 12px 16px;
  background: ${COLORS.red.bg};
  color: ${COLORS.red.dark};
  font-size: 13px;
  border-bottom: 1px solid ${COLORS.red.border};
`;

const RunMeta = styled.div`
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #333;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const RunMetaItem = styled.span`
  strong {
    color: #555;
    margin-right: 4px;
  }
`;

const GridScroller = styled.div`
  flex: 1;
  overflow: auto;
  padding: 8px;
`;

const PivotTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th,
  td {
    padding: 0;
  }

  thead tr {
    background: #f8f9fa;
    border-bottom: 2px solid #e0e0e0;
  }

  tbody td {
    border-bottom: 1px solid #e8e8e8;
  }

  tbody tr:hover {
    background: #f9f9f9;
  }
`;

const MessageHeaderCell = styled.th`
  text-align: left;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  min-width: 180px;
  position: sticky;
  left: 0;
  background: #f8f9fa;
  z-index: 2;
`;

const SegmentHeaderCell = styled.th`
  text-align: center;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  min-width: 120px;
`;

const MessageNameCell = styled.td`
  padding: 10px 8px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  min-width: 180px;
`;

const MessageName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  cursor: default;
  display: block;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
`;

const PendingCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  color: #bbb;
  font-size: 14px;
`;

const ErrorCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 8px 6px;
  margin: 4px;
  border-radius: 4px;
  background: ${COLORS.red.bg};
  border: 1px solid ${COLORS.red.border};
  color: ${COLORS.red.text};
  font-size: 11px;
  font-weight: 600;
  cursor: default;
`;

const BadgeContainer = styled.div<{ $score: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  min-height: 48px;
  cursor: pointer;
  background: ${(p) => scoreBg(p.$score)};
  border-radius: 4px;
  margin: 4px;
  border: 1px solid ${(p) => scoreBorder(p.$score)};
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 0 2px ${(p) => scoreBorder(p.$score)};
  }
`;

const BadgeScore = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
`;

const BadgeVerdict = styled.div`
  font-size: 9px;
  letter-spacing: 0.3px;
  margin-top: 2px;
  color: #666;
`;

// ─── Popover styled ─────────────────────────────────────────────────────────

const PopoverContent = styled.div`
  padding: 16px;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

function verdictColorLight(verdict: string): string {
  switch (verdict) {
    case 'strong_fit':
      return COLORS.green.text;
    case 'workable':
      return COLORS.yellow.text;
    case 'weak_fit':
      return COLORS.red.text;
    case 'do_not_use':
      return COLORS.red.dark;
    default:
      return '#424242';
  }
}

const PopoverVerdict = styled.div<{ $verdict: string }>`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const PopoverOverallScore = styled.div<{ $score: number }>`
  font-size: 22px;
  font-weight: 800;
  color: ${(p) => scoreColor(p.$score)};
`;

const PopoverSummary = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.4;
  margin-bottom: 12px;
`;

const PopoverSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: var(--black-l);
  margin: 12px 0 6px;
  padding-top: 8px;
  border-top: 1px solid #eee;
`;

const PopoverSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PopoverCallout = styled.div<{ $type: 'strength' | 'weakness' }>`
  font-size: 11px;
  line-height: 1.4;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${(p) => (p.$type === 'strength' ? COLORS.green.bg : COLORS.red.bg)};

  strong {
    font-weight: 600;
  }
`;

const PopoverDetail = styled.div`
  font-size: 11px;
  color: #555;
  line-height: 1.4;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoresColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 12px;
`;

const ScoreCompactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 0;
`;

const ScoreCompactLabel = styled.div`
  font-size: 10px;
  color: #666;
`;

const ScoreCompactValue = styled.div<{ $score: number }>`
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => scoreColor(p.$score)};
`;

/* Kept for reference — replaced by ScoresColumns
const ScoresGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ScoreItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScoreItemLabel = styled.div`
  font-size: 10px;
  color: #666;
  width: 110px;
  flex-shrink: 0;
`;

const ScoreItemBar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #f0f0f0;
  overflow: hidden;
`;

const ScoreItemFill = styled.div<{ $score: number }>`
  height: 100%;
  border-radius: 3px;
  background: ${(p) => scoreColor(p.$score)};
  transition: width 0.3s ease;
`;

const ScoreItemValue = styled.div<{ $score: number }>`
  font-size: 11px;
  font-weight: 700;
  width: 28px;
  text-align: right;
  color: ${(p) => scoreColor(p.$score)};
`;
*/

// ─── Score Item Popover styled ──────────────────────────────────────────────

const ScorePopoverContent = styled.div`
  padding: 14px;
`;

const ScorePopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const ScorePopoverLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
`;

const ScorePopoverValue = styled.div<{ $score: number }>`
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => scoreColor(p.$score)};
`;

const ScoreConfidence = styled.div`
  font-size: 11px;
  color: #666;
  margin-bottom: 6px;

  strong {
    color: #1a1a1a;
    font-weight: 600;
  }
`;

const ScoreReasoning = styled.div`
  font-size: 11px;
  color: #555;
  line-height: 1.45;
  margin-bottom: 4px;
`;

const ScorePopoverSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  margin: 10px 0 5px;
  padding-top: 6px;
  border-top: 1px solid #eee;
`;

function impactBorderColor(impact: string): string {
  switch (impact) {
    case 'positive':
      return COLORS.green.accent;
    case 'negative':
      return COLORS.red.accent;
    default:
      return COLORS.neutral.border;
  }
}

const EvidenceItem = styled.div<{ $impact: string }>`
  padding: 5px 8px;
  margin: 3px 0;
  border-left: 3px solid ${(p) => impactBorderColor(p.$impact)};
  background: #fafafa;
  border-radius: 0 4px 4px 0;
`;

const EvidenceQuote = styled.div`
  font-size: 11px;
  font-style: italic;
  color: #333;
  line-height: 1.4;
`;

const EvidenceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
`;

const EvidenceSource = styled.span`
  font-size: 9px;
  color: #888;
  text-transform: capitalize;
`;

function impactColor(impact: string): string {
  switch (impact) {
    case 'positive':
      return COLORS.green.text;
    case 'negative':
      return COLORS.red.text;
    default:
      return COLORS.neutral.text;
  }
}

function impactBg(impact: string): string {
  switch (impact) {
    case 'positive':
      return COLORS.green.bg;
    case 'negative':
      return COLORS.red.bg;
    default:
      return COLORS.neutral.bg;
  }
}

const EvidenceImpact = styled.span<{ $impact: string }>`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  color: ${(p) => impactColor(p.$impact)};
  background: ${(p) => impactBg(p.$impact)};
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const AttributeChip = styled.span<{ $type: 'invoked' | 'unused' }>`
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 10px;
  background: ${(p) => (p.$type === 'invoked' ? 'rgb(229, 245, 252)' : COLORS.neutral.bg)};
  color: ${(p) => (p.$type === 'invoked' ? 'var(--black-l)' : COLORS.neutral.text)};
`;

const TriggerItem = styled.div<{ $type: 'positive' | 'negative' }>`
  padding: 4px 8px;
  border-left: 3px solid ${(p) => (p.$type === 'positive' ? COLORS.green.accent : COLORS.red.accent)};
  margin: 2px 0;
`;

const TriggerPhrase = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
  font-style: italic;
`;

const TriggerReason = styled.div`
  font-size: 10px;
  color: #888;
  line-height: 1.3;
`;

const CollapsibleSection = styled.details`
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 4px;
`;

const CollapsibleSummary = styled.summary`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #999;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;

  &:hover {
    color: #666;
  }

  &::marker {
    font-size: 10px;
  }
`;

const ObjectionItem = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return COLORS.red.dark;
    case 'high':
      return COLORS.red.text;
    case 'medium':
      return COLORS.yellow.text;
    case 'low':
      return COLORS.green.text;
    default:
      return '#666';
  }
}

const ObjectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #444;
  text-transform: capitalize;
  margin-bottom: 2px;
`;

const ObjectionSeverity = styled.span<{ $severity: string }>`
  font-style: italic;
  font-weight: 500;
  color: ${(p) => severityColor(p.$severity)};
  margin-left: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 13px;
  color: #666;
`;
